import type { ViewOption } from '@/engine/app/interfaces';
import { type FC } from 'react';
import TableRow from '@mui/material/TableRow';
import type { ViewOptionsController } from './ViewOptionsController';
import { ColorMarker } from '@/components/viewOption/ColorMarker.tsx';
import { observer } from 'mobx-react';
import { EyeCheckboxRender } from '../common/expandableTable/EyeCheckboxRender.tsx';
import { ContoursCheckboxRender } from './icon-renderers/ContoursCheckboxrender';
import { TransparencyIconRender } from './icon-renderers/TransparencyIconRender';
import { ViewOptionsTableRow } from './ViewOptionsTableRow';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';

export const ViewOptionsObject: FC<{
  controller: ViewOptionsController;
  viewOption: ViewOption;
  disabledVisibility?: boolean;
}> = observer(({ controller, viewOption, disabledVisibility }) => {
  if (!controller.isObjectAvailable(viewOption)) {
    return <TableRow key={viewOption.label} />;
  }
  const label = controller.getLabel(viewOption);

  return (
    <ViewOptionsTableRow
      contentCell={
        <ViewOptionObject>
          <StyledColorMarker color={controller.color(viewOption)} data-testid="color-marker" />
          <Tooltip
            title={label}
            placement="top"
            slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -14] } }] } }}
          >
            <Label>{label}</Label>
          </Tooltip>
        </ViewOptionObject>
      }
      contourCell={
        <ContoursCheckboxRender
          onChange={(isContourVisible): void => controller.setContourVisibility(viewOption, isContourVisible)}
          isChecked={controller.isContourVisible(viewOption)}
        />
      }
      transparencyCell={
        <TransparencyIconRender
          transparency={controller.transparency(viewOption)}
          onClick={(transparency): void => controller.setTransparency(viewOption, transparency)}
        />
      }
      visibilityCell={
        <EyeCheckboxRender
          onChange={(isVisible): void => controller.setVisibility(viewOption, isVisible)}
          isChecked={controller.isVisible(viewOption)}
          isDisabled={disabledVisibility}
        />
      }
      testId={`${controller.getLabel(viewOption)}-view-option`}
      hover
    />
  );
});

const StyledColorMarker = styled(ColorMarker)`
  margin-left: 0.5rem;
`;

const ViewOptionObject = styled.div`
  display: flex;
  gap: 1rem;
`;

const Label = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 9.5rem;
`;
